import { useConversationScrollFollow } from "./useConversationScrollFollow";
import { ConversationUserCard } from "./ConversationUserCard";
import { MerryQuoteDisclosure } from "@/shared/ui/MerryQuoteDisclosure";
import { FormalConversationView } from "./FormalConversationView";
import { useGSAP } from "@gsap/react";
import { useChat } from "@ai-sdk/react";
import type { ChatSessionPersistedState } from "@trigger.dev/sdk/chat";
import { useTriggerChatTransport } from "@trigger.dev/sdk/chat/react";
import gsap from "gsap";
import {
  useCallback,
  useEffect,
  useEffectEvent,
  useMemo,
  useRef,
  useState,
} from "react";
import { Dialog, DialogTrigger, Popover, Button as AriaButton } from "react-aria-components";
import { useLocation, useNavigate } from "react-router";
import type { UIMessage } from "ai";

import "@/features/experience-strategy/experienceStrategyConversation.css";

import { useAppDispatch } from "@/app/hooks";
import type { ChatAttachment } from "@/features/chat/chatAttachments";
import { ChatAddMenuComposer } from "@/features/chat/ChatAddMenuComposer";
import type { ChatAddMenuSendContext } from "@/features/chat/ChatAddMenuComposer";
import {
  useGetConversationExecutionNodesQuery,
  useGetConversationFreshTasksQuery,
  useGetConversationWorkflowsQuery,
  useLazyGetConversationWorkflowEventsQuery,
  useGetConversationMessagesInfiniteQuery,
  useGetConversationQuery,
  useGetConversationTurnsInfiniteQuery,
  useMintConversationAccessTokenMutation,
  useRetryConversationWorkflowStepMutation,
  useStartConversationSessionMutation,
  type ConversationScope,
} from "@/features/chat/conversationApi";
import {
  buildConversationQuote,
  buildConversationRenderTurns,
  buildMinimapPreview,
  flattenConversationMessagePages,
  flattenConversationTurnPages,
  mergeConversationUIMessages,
  latestConversationResourceGate,
  latestFailedFinishConversationTurn,
  latestLengthLimitedConversationTurn,
  toChatUIMessage,
  type ConversationQuoteDraft,
} from "@/features/chat/conversationProjection";
import { ConversationAssistantResponse } from "@/features/chat/ConversationAssistantResponse";
import { ConversationFreshTaskStatus } from "@/features/chat/ConversationFreshTaskStatus";
import { ConversationWorkflowProgress } from "@/features/chat/ConversationWorkflowProgress";
import type { ConversationWorkflow } from "@/features/chat/conversationWorkflowSchemas";
import {
  acceptConversationWorkflowEvents,
  conversationWorkflowScopeKey,
  mergeConversationWorkflowProjection,
  workflowBelongsToScope,
} from "@/features/chat/conversationWorkflowProjection";
import { ConversationCreativeMediaProgress } from "./ConversationCreativeMediaProgress";
import { legacyCreativeMediaReferences, projectConversationMediaOwnership } from "./conversationMediaOwnership";
import { useConversationCreativeMediaProgress } from "./useConversationCreativeMediaProgress";
import { ConversationResourceLimitResult } from "@/features/chat/ConversationResourceLimitResult";
import {
  AssistantResponseSkeleton,
  ConversationHydrationSkeleton,
} from "@/features/chat/ConversationLoadingSkeleton";
import type { ConversationMessage } from "@/features/chat/conversationSchemas";
import {
  ConversationTurnReconciliationScheduler,
  clearConversationReconciliationTurn,
  conversationTurnReconciliationDecision,
} from "@/features/chat/conversationTurnReconciliation";
import { merchantContextApi } from "@/features/chat/merchantContextApi";
import { useConversationQuoteSelection } from "@/features/chat/useConversationQuoteSelection";
import { useConversationStickyState } from "@/features/chat/useConversationStickyState";
import { workspacePendingSendBelongsTo } from "@/features/chat/workspaceLaunchScope";
import {
  resourceAvailabilityDismissKey,
  selectResourceContextBar,
  selectWorkspaceResourceCapabilities,
} from "@/features/chat/resourceAvailability";
import { useGetResourceAvailabilityQuery } from "@/features/chat/resourceAvailabilityApi";
import {
  experienceStrategyAdjustmentPrompt,
  type ExperienceStrategyChatAdjustmentReference,
} from "@/features/experience-strategy/experienceStrategyAdjustment";
import {
  useGetConversationMessageWindowQuery,
  useGetConversationTurnSeekQuery,
} from "@/features/experience-strategy/experienceStrategyApi";
import { MerryChatContextBar } from "@/shared/ui/MerryChatContextBar";
import type { MerryChatExtensionViewModel } from "@/shared/ui/chatExtensionTypes";
import { MerchantHomeIntentStatus } from "@/features/merchant-home/MerchantHomeIntentStatus";
import { merchantHomeIntentOperationId } from "@/features/merchant-home/merchantHomeIntentReference";
import { ConversationUnassignedShopNotice } from "@/features/chat/ConversationUnassignedShopNotice";

const merchantChatTaskId = "merchant-chat";
const merchantChatAgentContractVersion = "cwm-v16";
const triggerApiUrl = (
  import.meta.env.VITE_TRIGGER_API_URL ?? "https://trigger.boattosea.cn"
).replace(/\/+$/, "");

export interface PendingWorkspaceSend {
  context: ChatAddMenuSendContext;
  id: string;
  strategyAdjustment?: ExperienceStrategyChatAdjustmentReference | null;
  target: ConversationScope;
  text: string;
}

interface WorkspaceConversationProps {
  attachments: ReadonlyArray<ChatAttachment>;
  conversationId: string;
  onAttachmentsChange: (attachments: ReadonlyArray<ChatAttachment>) => void;
  onReset: () => void;
  onRetryAttachment: (attachmentId: string) => void;
  onUploadFiles: (files: ReadonlyArray<File>) => string | null | void;
  onPendingSendConsumed: (id: string) => void;
  onPendingSendSucceeded?: () => void;
  onStrategyAdjustmentConsumed: () => void;
  organizationId: string;
  shopBindingId?: string;
  pendingSend: PendingWorkspaceSend | null;
  presentation?: "workspace" | "temporary-store-overview";
  strategyAdjustment: ExperienceStrategyChatAdjustmentReference | null;
  targetSequence: number | null;
  isUploadDisabled: boolean;
  userId: string;
}

export function WorkspaceConversation(props: WorkspaceConversationProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const scope = useMemo<ConversationScope>(
    () => ({
      conversationId: props.conversationId,
      organizationId: props.organizationId,
      shopBindingId: props.shopBindingId ?? "",
      userId: props.userId,
    }),
    [props.conversationId, props.organizationId, props.shopBindingId, props.userId],
  );
  const conversationQuery = useGetConversationQuery(scope);
  const messagesQuery = useGetConversationMessagesInfiniteQuery(scope);
  const seekQuery = useGetConversationTurnSeekQuery(
    {
      conversationId: props.conversationId,
      organizationId: props.organizationId,
      shopBindingId: props.shopBindingId ?? "",
      sequence: props.targetSequence ?? 1,
    },
    { skip: !props.targetSequence },
  );
  const targetThroughSequence = seekQuery.data
    ? Number(seekQuery.data.page.through_sequence)
    : null;
  const messageWindowQuery = useGetConversationMessageWindowQuery(
    {
      beforeSequence:
        targetThroughSequence && Number.isSafeInteger(targetThroughSequence)
          ? targetThroughSequence + 1
          : 1,
      conversationId: props.conversationId,
      organizationId: props.organizationId,
      shopBindingId: props.shopBindingId ?? "",
    },
    {
      skip:
        !props.targetSequence ||
        !targetThroughSequence ||
        !Number.isSafeInteger(targetThroughSequence),
    },
  );

  if (
    conversationQuery.isLoading ||
    messagesQuery.isLoading ||
    (props.targetSequence && seekQuery.isLoading) ||
    (targetThroughSequence && messageWindowQuery.isLoading)
  ) {
    return <ConversationHydrationSkeleton />;
  }
  if (!conversationQuery.data || !messagesQuery.data) {
    return (
      <section className="merry-conversation merry-conversation--status">
        <p role="alert">这段对话不存在，或当前账号无权查看。</p>
        <div>
          <AriaButton
            className="merry-conversation__status-action"
            onPress={props.onReset}
          >
            返回首页
          </AriaButton>
          <AriaButton
            className="merry-conversation__status-action"
            onPress={() => navigate("/chat-history", { replace: true })}
          >
            查看当前店铺历史
          </AriaButton>
        </div>
      </section>
    );
  }

  const initialHistory = mergeConversationHistory(
    flattenConversationMessagePages(messagesQuery.data),
    messageWindowQuery.data?.messages ?? [],
  );
  const initialTargetTurn = seekQuery.data
    ? {
        id: seekQuery.data.turn.turn_id,
        startSequence: Number(seekQuery.data.turn.start_sequence),
      }
    : null;
  return (
    <WorkspaceConversationRuntime
      {...props}
      conversationTitle={conversationQuery.data.conversation.title}
      frozenShopBindingId={conversationQuery.data.conversation.shopBindingId}
      initialHistory={initialHistory}
      initialTargetTurn={initialTargetTurn}
      merchantHomeOperationId={merchantHomeIntentOperationId(
        conversationQuery.currentData?.conversation.metadata,
        location.state,
      )}
      key={`${props.userId}:${props.organizationId}:${props.shopBindingId ?? ""}:${props.conversationId}`}
      scope={scope}
      sourceSeekUnavailable={Boolean(props.targetSequence && seekQuery.isError)}
    />
  );
}

interface WorkspaceConversationRuntimeProps extends WorkspaceConversationProps {
  conversationTitle: string;
  frozenShopBindingId: string | null;
  initialHistory: ReadonlyArray<ConversationMessage>;
  initialTargetTurn: { id: string; startSequence: number } | null;
  merchantHomeOperationId: string | null;
  scope: ConversationScope;
  sourceSeekUnavailable: boolean;
}

function WorkspaceConversationRuntime({
  attachments,
  conversationTitle,
  frozenShopBindingId,
  initialHistory,
  initialTargetTurn,
  merchantHomeOperationId,
  onAttachmentsChange,
  onPendingSendConsumed,
  onPendingSendSucceeded,
  onReset,
  onRetryAttachment,
  onStrategyAdjustmentConsumed,
  onUploadFiles,
  pendingSend,
  presentation = "workspace",
  isUploadDisabled,
  scope,
  sourceSeekUnavailable,
  strategyAdjustment,
}: WorkspaceConversationRuntimeProps) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const scrollRef = useRef<HTMLDivElement>(null);
  const { followLatest, pauseFollowing } = useConversationScrollFollow(scrollRef);
  const titleRef = useRef<HTMLElement>(null);
  const titleSentinelRef = useRef<HTMLDivElement>(null);
  const sentPendingIdsRef = useRef(new Set<string>());
  const initialPendingSendRef = useRef(pendingSend);
  const submittedAttachmentsRef = useRef<ReadonlyArray<ChatAttachment>>([]);
  const [failedInitialSend, setFailedInitialSend] = useState<PendingWorkspaceSend | null>(null);
  const [focusRequestKey, setFocusRequestKey] = useState(0);
  const [pendingQuote, setPendingQuote] = useState<ConversationQuoteDraft | null>(
    null,
  );
  const [targetTurn, setTargetTurn] = useState<{
    id: string;
    startSequence: number;
  } | null>(initialTargetTurn);
  const [highlightedTurnId, setHighlightedTurnId] = useState("");
  const [pendingStrategyAdjustment, setPendingStrategyAdjustment] =
    useState<ExperienceStrategyChatAdjustmentReference | null>(
      strategyAdjustment,
    );
  const [dismissedResourceKey, setDismissedResourceKey] = useState("");
  const [retryingMessageId, setRetryingMessageId] = useState("");
  const [retryingWorkflowStepId, setRetryingWorkflowStepId] = useState("");
  const [workflowRetryNotice, setWorkflowRetryNotice] = useState("");
  const [workflowRetryNoticeId, setWorkflowRetryNoticeId] = useState("");
  const [continuationNotice, setContinuationNotice] = useState("");
  const continuationInFlightRef = useRef("");
  const reconciliationSchedulerRef =
    useRef<ConversationTurnReconciliationScheduler | null>(null);
  const activeOutboundMessageIdRef = useRef("");
  const reconciliationGenerationRef = useRef(0);
  const [reconciliationTurnId, setReconciliationTurnId] = useState("");
  const [reconciliationTimedOutTurnId, setReconciliationTimedOutTurnId] =
    useState("");
  const [reconciliationRetryKey, setReconciliationRetryKey] = useState(0);
  const [sessions, setSessions] = useState<
    Record<string, ChatSessionPersistedState>
  >({});
  const [mintAccessToken] = useMintConversationAccessTokenMutation();
  const [retryWorkflowStep] = useRetryConversationWorkflowStepMutation();
  const [loadWorkflowEvents] = useLazyGetConversationWorkflowEventsQuery();
  const [startSession] = useStartConversationSessionMutation();
  const messagesQuery = useGetConversationMessagesInfiniteQuery(scope);
  const turnsQuery = useGetConversationTurnsInfiniteQuery(scope);
  const executionNodesQuery = useGetConversationExecutionNodesQuery(scope);
  const workflowsQuery = useGetConversationWorkflowsQuery(scope, {
    pollingInterval: presentation === "workspace" ? 4_000 : 0,
    skip: presentation !== "workspace",
    skipPollingIfUnfocused: true,
  });

  const workflowScopeKey = conversationWorkflowScopeKey(scope);
  const workflowEventCursorRef = useRef(new Map<string, number>());
  const workflowScopeKeyRef = useRef(workflowScopeKey);
  const [workflowProjection, setWorkflowProjection] = useState<{
    scopeKey: string;
    workflows: ConversationWorkflow[];
  }>({ scopeKey: workflowScopeKey, workflows: [] });

  useEffect(() => {
    workflowScopeKeyRef.current = workflowScopeKey;
    workflowEventCursorRef.current.clear();
  }, [workflowScopeKey]);

  useEffect(() => {
    const snapshots = workflowsQuery.currentData?.workflows;
    if (!snapshots) return;
    for (const workflow of snapshots) {
      if (!workflowBelongsToScope(workflow, scope)) continue;
      const currentRevision = workflowEventCursorRef.current.get(workflow.workflow_id) ?? 0;
      if (workflow.revision > currentRevision) {
        workflowEventCursorRef.current.set(workflow.workflow_id, workflow.revision);
      }
    }
  }, [
    scope,
    workflowsQuery.currentData,
  ]);
  const freshTasksQuery = useGetConversationFreshTasksQuery(scope, {
    pollingInterval: presentation === "workspace" ? 4_000 : 0,
    skip: presentation !== "workspace",
    skipPollingIfUnfocused: true,
  });
  const {
    fetchNextPage: fetchNextTurnPage,
    hasNextPage: hasNextTurnPage,
    isFetchingNextPage: isFetchingNextTurnPage,
  } = turnsQuery;
  const {
    fetchNextPage: fetchNextMessagePage,
    hasNextPage: hasNextMessagePage,
    isFetchingNextPage: isFetchingNextMessagePage,
  } = messagesQuery;
  const resourceAvailabilityQuery = useGetResourceAvailabilityQuery(
    scope.organizationId,
  );
  const restoreSubmittedAttachments = useCallback(() => {
    const submittedAttachments = submittedAttachmentsRef.current;
    if (submittedAttachments.length === 0) return;
    submittedAttachmentsRef.current = [];
    queueMicrotask(() => onAttachmentsChange(submittedAttachments));
  }, [onAttachmentsChange]);

  useEffect(() => {
    if (!initialTargetTurn) return;
    const frame = window.requestAnimationFrame(() => {
      setTargetTurn(initialTargetTurn);
    });
    return () => window.cancelAnimationFrame(frame);
  }, [initialTargetTurn]);

  useEffect(() => {
    if (!strategyAdjustment) return;
    const frame = window.requestAnimationFrame(() => {
      setPendingStrategyAdjustment(strategyAdjustment);
    });
    return () => window.cancelAnimationFrame(frame);
  }, [strategyAdjustment]);

  useEffect(() => {
    if (!highlightedTurnId) return;
    const timer = window.setTimeout(() => setHighlightedTurnId(""), 1_800);
    return () => window.clearTimeout(timer);
  }, [highlightedTurnId]);
  const resourceCapabilities = selectWorkspaceResourceCapabilities(
    resourceAvailabilityQuery.data,
  );
  const history = useMemo(
    () =>
      messagesQuery.data
        ? mergeConversationHistory(
            flattenConversationMessagePages(messagesQuery.data),
            initialHistory,
          )
        : [...initialHistory],
    [initialHistory, messagesQuery.data],
  );
  const serverTurns = useMemo(
    () => flattenConversationTurnPages(turnsQuery.data),
    [turnsQuery.data],
  );
  const reconciliationScopeKey = JSON.stringify([
    scope.userId,
    scope.organizationId,
    scope.shopBindingId,
    scope.conversationId,
  ]);

  const transport = useTriggerChatTransport({
    accessToken: async ({ chatId }) => {
      if (chatId !== scope.conversationId) {
        throw new Error("对话身份不一致，请刷新后重试");
      }
      const result = await mintAccessToken(scope).unwrap();
      return result.publicAccessToken;
    },
    baseURL: triggerApiUrl,
    clientData: {
      agentContractVersion: merchantChatAgentContractVersion,
      conversationId: scope.conversationId,
      organizationId: scope.organizationId,
      shopBindingId: scope.shopBindingId,
      userId: scope.userId,
    },
    onSessionChange: (chatId, session) => {
      if (chatId !== scope.conversationId) return;
      setSessions((current) => {
        if (!session) {
          const next = { ...current };
          delete next[chatId];
          return next;
        }
        return { ...current, [chatId]: session };
      });
    },
    sessions,
    startSession: async ({ chatId, taskId }) => {
      if (chatId !== scope.conversationId) {
        throw new Error("对话身份不一致，请刷新后重试");
      }
      const result = await startSession({
        ...scope,
        clientData: {
          agentContractVersion: merchantChatAgentContractVersion,
          conversationId: scope.conversationId,
          organizationId: scope.organizationId,
          shopBindingId: scope.shopBindingId,
          userId: scope.userId,
        },
        taskId,
      }).unwrap();
      return { publicAccessToken: result.publicAccessToken };
    },
    task: merchantChatTaskId,
  });

  const initialMessages = useMemo(
    () => initialHistory.flatMap((item) => toChatUIMessage(item) ?? []),
    [initialHistory],
  );
  const {
    error: chatError,
    messages: liveMessages,
    regenerate,
    sendMessage,
    setMessages,
    status,
    stop,
  } = useChat<UIMessage>({
    id: scope.conversationId,
    messages: initialMessages,
    onError: () => {
      restoreSubmittedAttachments();
      const failedTurnId = activeOutboundMessageIdRef.current;
      activeOutboundMessageIdRef.current = "";
      if (!failedTurnId) return;
      setReconciliationTurnId((current) =>
        clearConversationReconciliationTurn(current, failedTurnId),
      );
    },
    onFinish: () => {
      activeOutboundMessageIdRef.current = "";
      submittedAttachmentsRef.current = [];
      // 终态追赶调度器统一拥有 messages/turns/nodes 的串行读取；
      // 这里仅请求立即执行，避免与调度批次再并发一组相同 GET。
      reconciliationSchedulerRef.current?.requestNow();
      if (!workflowsQuery.isUninitialized) void workflowsQuery.refetch();
      if (!freshTasksQuery.isUninitialized) void freshTasksQuery.refetch();
      void resourceAvailabilityQuery.refetch();
      dispatch(
        merchantContextApi.util.invalidateTags([
          {
            id: `${scope.organizationId}:${scope.conversationId}`,
            type: "MerchantContext",
          },
        ]),
      );
    },
    resume: initialMessages.length > 0,
    transport,
  });

  useEffect(() => {
    if (status === "streaming" || status === "submitted") return;
    setMessages((current) => mergeConversationUIMessages(history, current, { preferPersisted: true }));
  }, [history, setMessages, status]);

  const readReconciliation = useEffectEvent(
    (epoch: number, turnId: string) => {
      const messageRequest = messagesQuery.refetch();
      const turnRequest = turnsQuery.refetch();
      const executionRequest = executionNodesQuery.refetch();
      const requests = [messageRequest, turnRequest, executionRequest] as const;
      return {
        abort: () => {
          for (const request of requests) request.abort();
        },
        settled: Promise.allSettled(requests).then((results) => {
          if (
            epoch !== reconciliationGenerationRef.current ||
            document.hidden
          ) {
            return { continuePolling: false };
          }
          const messageResult = results[0];
          const turnResult = results[1];
          if (
            messageResult.status !== "fulfilled" ||
            turnResult.status !== "fulfilled" ||
            !messageResult.value.data ||
            !turnResult.value.data
          ) {
            return { continuePolling: true };
          }
          const persistedMessages = flattenConversationMessagePages(
            messageResult.value.data,
          );
          const persistedTurns = flattenConversationTurnPages(
            turnResult.value.data,
          );
          setMessages((current) =>
            mergeConversationUIMessages(persistedMessages, current, {
              preferPersisted: true,
            }),
          );
          const decision = conversationTurnReconciliationDecision({
            messages: persistedMessages,
            turnId,
            turns: persistedTurns,
          });
          if (decision === "continue") return { continuePolling: true };
          setReconciliationTurnId((current) =>
            current === turnId ? "" : current,
          );
          setReconciliationTimedOutTurnId((current) =>
            current === turnId ? "" : current,
          );
          return { continuePolling: false };
        }),
      };
    },
  );

  useEffect(() => {
    if (!reconciliationTurnId) return;
    const epoch = ++reconciliationGenerationRef.current;
    const scheduler = new ConversationTurnReconciliationScheduler(
      () => readReconciliation(epoch, reconciliationTurnId),
      () => setReconciliationTimedOutTurnId(reconciliationTurnId),
    );
    reconciliationSchedulerRef.current = scheduler;
    if (document.hidden) scheduler.pause();
    else scheduler.requestNow();
    const handleVisibility = () => {
      if (document.hidden) scheduler.pause();
      else scheduler.resume();
    };
    const refresh = () => {
      if (!document.hidden) scheduler.requestNow();
    };
    document.addEventListener("visibilitychange", handleVisibility);
    window.addEventListener("focus", refresh);
    window.addEventListener("online", refresh);
    return () => {
      reconciliationSchedulerRef.current = null;
      reconciliationGenerationRef.current += 1;
      scheduler.dispose();
      document.removeEventListener("visibilitychange", handleVisibility);
      window.removeEventListener("focus", refresh);
      window.removeEventListener("online", refresh);
    };
  }, [reconciliationRetryKey, reconciliationScopeKey, reconciliationTurnId]);

  useEffect(() => {
    if (reconciliationTurnId) return;
    const latestTurn = serverTurns.at(-1);
    if (
      !latestTurn ||
      conversationTurnReconciliationDecision({
        messages: history,
        turnId: latestTurn.userMessageId,
        turns: [latestTurn],
      }) !== "continue"
    ) return;
    let cancelled = false;
    queueMicrotask(() => {
      if (!cancelled) {
        setReconciliationTurnId((current) =>
          current || latestTurn.userMessageId,
        );
      }
    });
    return () => {
      cancelled = true;
    };
  }, [history, reconciliationTurnId, serverTurns]);

  const reconciliationTimedOut = Boolean(
    reconciliationTurnId &&
      reconciliationTimedOutTurnId === reconciliationTurnId,
  );
  const isConversationProcessing =
    status === "submitted" ||
    status === "streaming" ||
    Boolean(reconciliationTurnId && !reconciliationTimedOut);

  useEffect(() => {
    if (hasNextTurnPage && !isFetchingNextTurnPage) {
      void fetchNextTurnPage();
    }
  }, [
    fetchNextTurnPage,
    hasNextTurnPage,
    isFetchingNextTurnPage,
    turnsQuery.data?.pages.length,
  ]);

  const renderTurns = useMemo(
    () =>
      buildConversationRenderTurns(
        history,
        liveMessages,
        executionNodesQuery.data?.executionNodes ?? [],
        { isGenerating: isConversationProcessing },
      ),
    [executionNodesQuery.data?.executionNodes, history, isConversationProcessing, liveMessages],
  );
  const lengthLimitedTurn = useMemo(
    () => latestLengthLimitedConversationTurn(serverTurns, history),
    [history, serverTurns],
  );
  const failedFinishTurn = useMemo(
    () => latestFailedFinishConversationTurn(serverTurns, history),
    [history, serverTurns],
  );
  const continuationSubmittedUserId = lengthLimitedTurn ? liveMessages.find(
    (message) => message.role === "user" &&
      message.metadata && typeof message.metadata === "object" &&
      "continuation_of_turn_id" in message.metadata &&
      message.metadata.continuation_of_turn_id === lengthLimitedTurn.turnId,
  )?.id ?? "" : "";
  const continuationAlreadySubmitted = Boolean(continuationSubmittedUserId);
  const continuationCanRetry = Boolean(
    continuationSubmittedUserId && (chatError || continuationNotice),
  );
  const mediaProgressEnabled = presentation === "workspace";
  const conversationWorkflows = useMemo(() => mergeConversationWorkflowProjection(
    workflowProjection.scopeKey === workflowScopeKey ? workflowProjection.workflows : [],
    workflowsQuery.currentData?.workflows ?? [],
    scope,
  ), [scope, workflowProjection, workflowScopeKey, workflowsQuery.currentData]);
  const activeWorkflowPollKey = conversationWorkflows
    .filter((workflow) => workflow.status === "running" || workflow.status === "waiting_approval")
    .map((workflow) => `${workflow.workflow_id}:${workflow.revision}`)
    .sort()
    .join("|");

  useEffect(() => {
    if (presentation !== "workspace" || !activeWorkflowPollKey) return;
    let cancelled = false;
    let inFlight = false;
    const synchronize = async () => {
      if (inFlight || cancelled || workflowScopeKeyRef.current !== workflowScopeKey) return;
      inFlight = true;
      const activeWorkflows = conversationWorkflows
        .filter((workflow) => workflow.status === "running" || workflow.status === "waiting_approval");
      try {
        for (const workflow of activeWorkflows) {
          if (cancelled || workflowScopeKeyRef.current !== workflowScopeKey) break;
          const afterRevision = Math.max(
            workflow.revision,
            workflowEventCursorRef.current.get(workflow.workflow_id) ?? 0,
          );
          workflowEventCursorRef.current.set(workflow.workflow_id, afterRevision);
          try {
            const response = await loadWorkflowEvents({
              ...scope,
              workflowId: workflow.workflow_id,
              afterRevision,
            }).unwrap();
            if (cancelled || workflowScopeKeyRef.current !== workflowScopeKey ||
              workflowEventCursorRef.current.get(workflow.workflow_id) !== afterRevision) continue;
            const responseAcceptance = acceptConversationWorkflowEvents(
              conversationWorkflows, response, scope, workflow.workflow_id, afterRevision,
            );
            if (!responseAcceptance.accepted) continue;
            // Advance transport bookkeeping outside React's replayable state updater.
            workflowEventCursorRef.current.set(workflow.workflow_id, responseAcceptance.currentRevision);
            setWorkflowProjection((current) => {
              if (cancelled || workflowScopeKeyRef.current !== workflowScopeKey) return current;
              const accepted = acceptConversationWorkflowEvents(
                current.scopeKey === workflowScopeKey ? current.workflows : [],
                response,
                scope,
                workflow.workflow_id,
                afterRevision,
              );
              if (!accepted.accepted) return current;
              return { scopeKey: workflowScopeKey, workflows: accepted.workflows };
            });
          } catch {
            // Snapshot 轮询继续作为兜底；增量读取失败不能清空已确认的终态。
          }
        }
      } finally {
        inFlight = false;
      }
    };
    void synchronize();
    const timer = window.setInterval(() => { void synchronize(); }, 1_500);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [
    activeWorkflowPollKey,
    loadWorkflowEvents,
    presentation,
    scope,
    conversationWorkflows,
    workflowScopeKey,
  ]);
  const workflowsByTurn = useMemo(() => {
    const byTurn = new Map<string, ConversationWorkflow>();
    for (const workflow of conversationWorkflows ?? []) {
      const current = byTurn.get(workflow.turn_id);
      if (!current || workflow.revision > current.revision) {
        byTurn.set(workflow.turn_id, workflow);
      }
    }
    return byTurn;
  }, [conversationWorkflows]);
  const mediaLegacyReferences = useMemo(() => legacyCreativeMediaReferences(history), [history]);
  const mediaReferences = useMemo(() => projectConversationMediaOwnership(renderTurns, [], mediaLegacyReferences, mediaProgressEnabled),
    [renderTurns, mediaLegacyReferences, mediaProgressEnabled]);
  const mediaProgress = useConversationCreativeMediaProgress({
    ...scope, originTurnIds: renderTurns.map((turn) => turn.id), jobIds: mediaReferences.jobIds,
  }, isConversationProcessing, mediaProgressEnabled);
  const mediaOwnership = useMemo(() => projectConversationMediaOwnership(renderTurns, mediaProgress.samples, mediaLegacyReferences, mediaProgressEnabled),
    [renderTurns, mediaLegacyReferences, mediaProgress.samples, mediaProgressEnabled]);
  const openMediaCanvas = (jobId: string, resourceId?: string) => navigate(
    `/workspace/conversations/${encodeURIComponent(scope.conversationId)}/creative-media/${encodeURIComponent(jobId)}`,
    { state: { conversationId: scope.conversationId, initialResourceId: resourceId ?? "" } },
  );
  const openCloudResource = (resourceId: string) => {
    const params = new URLSearchParams({ drawer: "my-files", resource: resourceId });
    navigate(`/workspace?${params.toString()}`);
  };
  const retryFailedWorkflowStep = async (
    workflow: ConversationWorkflow,
    step: ConversationWorkflow["steps"][number],
  ) => {
    if (!step.retryable || !step.retry_supported || retryingWorkflowStepId) return;
    setWorkflowRetryNotice("");
    setWorkflowRetryNoticeId("");
    setRetryingWorkflowStepId(step.step_id);
    try {
      await retryWorkflowStep({
        ...scope,
        expectedAttempt: step.attempt + 1,
        expectedStepRevision: step.revision,
        expectedWorkflowRevision: workflow.revision,
        stepId: step.step_id,
        workflowId: workflow.workflow_id,
      }).unwrap();
      await workflowsQuery.refetch();
    } catch {
      setWorkflowRetryNotice("重试未能提交，状态可能已经变化，请重新读取后再试。");
      setWorkflowRetryNoticeId(workflow.workflow_id);
    } finally {
      setRetryingWorkflowStepId("");
    }
  };
  const latestCompletedAssistantId = useMemo(() => {
    const message = renderTurns.at(-1)?.assistantMessages.at(-1);
    if (!message || message.id === lengthLimitedTurn?.assistantMessageId ||
      message.executionNodes.some((node) =>
        ["queued", "running", "waiting_input", "approval_required"].includes(node.status) ||
        (node.depth === 0 && (node.status === "failed" || node.status === "canceled")),
      )) return "";
    return message.id;
  }, [lengthLimitedTurn?.assistantMessageId, renderTurns]);
  const minimapItems = useMemo(
    () => {
      const persisted = serverTurns.map(buildMinimapPreview);
      const known = new Set(serverTurns.map((turn) => turn.userMessageId));
      return [...persisted, ...renderTurns.filter((turn) => !known.has(turn.id)).map((turn) => ({
        id: turn.id,
        accessibleLabel: `跳转到对话：${turn.userMessage.text || "已发送附件"}`,
        preview: { meta: turn.userMessage.text || "已发送附件", summary: turn.assistantMessages.at(-1)?.text || "正在处理" },
      }))];
    },
    [renderTurns, serverTurns],
  );
  const turnSignature = renderTurns.map((turn) => turn.id).join("|");
  const stickyState = useConversationStickyState(
    scrollRef,
    titleSentinelRef,
    turnSignature,
  );
  const quoteSelection = useConversationQuoteSelection(scrollRef);

  useGSAP(
    () => {
      const title = titleRef.current;
      const root = scrollRef.current;
      if (!title || !root) return;
      const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      gsap.killTweensOf(title);
      gsap.to(title, {
        backgroundColor:
          stickyState.level >= 2 ? "rgba(255,255,255,0.72)" : "rgba(255,255,255,0)",
        boxShadow:
          stickyState.level >= 2
            ? "0 12px 32px rgba(25,38,43,0.08)"
            : "0 0 0 rgba(25,38,43,0)",
        duration: reduced ? 0 : 0.2,
        ease: "power2.out",
        overwrite: "auto",
      });
      const cards = [...root.querySelectorAll<HTMLElement>("[data-conversation-user-card]")];
      if (cards.length === 0) return;
      gsap.killTweensOf(cards);
      for (const card of cards) {
        const active =
          stickyState.level === 3 &&
          card.dataset.conversationUserCard === stickyState.activeTurnId;
        gsap.to(card, {
          backgroundColor: active ? "rgba(255,255,255,0.9)" : "rgba(255,255,255,0.54)",
          boxShadow: active
            ? "0 12px 28px rgba(25,38,43,0.09)"
            : "0 8px 24px rgba(25,38,43,0.04)",
          duration: reduced ? 0 : 0.18,
          ease: "power2.out",
          overwrite: "auto",
        });
      }
    },
    { dependencies: [stickyState.activeTurnId, stickyState.level], scope: scrollRef },
  );

  const loadOlderMessages = useCallback(async () => {
    const scroller = scrollRef.current;
    if (!scroller || !hasNextMessagePage || isFetchingNextMessagePage) {
      return;
    }
    pauseFollowing();
    const previousHeight = scroller.scrollHeight;
    const previousTop = scroller.scrollTop;
    await fetchNextMessagePage();
    await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
    await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
    // Explicit QA navigation owns alignment after hydration; do not overwrite its seek.
    if (!targetTurn) scroller.scrollTop = previousTop + (scroller.scrollHeight - previousHeight);
  }, [
    targetTurn,
    pauseFollowing,
    fetchNextMessagePage,
    hasNextMessagePage,
    isFetchingNextMessagePage,
  ]);

  useEffect(() => {
    if (!targetTurn) return;
    const target = document.getElementById(`conversation-turn-${targetTurn.id}`);
    if (target) {
      target.scrollIntoView({
        behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches
          ? "auto"
          : "smooth",
        block: "start",
      });
      requestAnimationFrame(() => {
        setHighlightedTurnId(targetTurn.id);
        setTargetTurn(null);
      });
      return;
    }
    const earliest = history[0]?.sequence ?? Number.POSITIVE_INFINITY;
    if (
      targetTurn.startSequence < earliest &&
      hasNextMessagePage &&
      !isFetchingNextMessagePage
    ) {
      void loadOlderMessages();
      return;
    }
    if (!hasNextMessagePage) {
      requestAnimationFrame(() => setTargetTurn(null));
    }
  }, [
    history,
    loadOlderMessages,
    hasNextMessagePage,
    isFetchingNextMessagePage,
    targetTurn,
  ]);

  const handleSend = useCallback(
    async (
      text: string,
      context: ChatAddMenuSendContext,
      adjustmentOverride?: ExperienceStrategyChatAdjustmentReference | null,
      pendingMessageId?: string,
    ) => {
      if (!resourceCapabilities.canSendAI) {
        throw new Error("AI 使用额度已用完，请充值后重试");
      }
      const quote = pendingQuote
        ? await buildConversationQuote(pendingQuote, scope)
        : null;
      const adjustment = adjustmentOverride ?? pendingStrategyAdjustment;
      const submittedResourceIds = new Set(
        context.resourceRefs.map((resource) => resource.resource_id),
      );
      submittedAttachmentsRef.current = attachments.filter(
        (attachment) =>
          attachment.resourceId !== null &&
          submittedResourceIds.has(attachment.resourceId),
      );
      const messageId = pendingMessageId ?? crypto.randomUUID();
      activeOutboundMessageIdRef.current = messageId;
      setReconciliationTimedOutTurnId("");
      setReconciliationTurnId(messageId);
      try {
        const sending = sendMessage({
          id: messageId,
          metadata: {
            ...(context.capabilityIntent
              ? { capability_intent: context.capabilityIntent }
              : {}),
            ...(context.extensions ? { extensions: context.extensions } : {}),
            ...(quote ? { quote } : {}),
            ...(adjustment
              ? { experience_strategy_adjustment: adjustment }
              : {}),
            resources: context.resourceRefs,
          },
          parts: [{ text, type: "text" }],
          role: "user",
        });
        followLatest();
        await sending;
      } catch (error) {
        // 同步失败由 composer 自己用发送前快照恢复；这里只保留异步 transport 错误的兜底。
        submittedAttachmentsRef.current = [];
        activeOutboundMessageIdRef.current = "";
        setReconciliationTurnId((current) =>
          clearConversationReconciliationTurn(current, messageId),
        );
        throw error;
      }
      setPendingQuote(null);
      if (adjustment) {
        setPendingStrategyAdjustment(null);
        onStrategyAdjustmentConsumed();
      }
    },
    [
      onStrategyAdjustmentConsumed,
      followLatest,
      attachments,
      pendingQuote,
      pendingStrategyAdjustment,
      resourceCapabilities.canSendAI,
      scope,
      sendMessage,
    ],
  );

  useEffect(() => {
    if (!pendingSend || sentPendingIdsRef.current.has(pendingSend.id)) return;
    let canceled = false;
    queueMicrotask(() => {
      if (canceled || sentPendingIdsRef.current.has(pendingSend.id)) return;
      sentPendingIdsRef.current.add(pendingSend.id);
      if (!workspacePendingSendBelongsTo(pendingSend.target, scope)) {
        initialPendingSendRef.current = null;
        onPendingSendConsumed(pendingSend.id);
        return;
      }
      void handleSend(
        pendingSend.text,
        pendingSend.context,
        pendingSend.strategyAdjustment,
        pendingSend.id,
      )
        .then(() => {
          onPendingSendSucceeded?.();
          onPendingSendConsumed(pendingSend.id);
        })
        .catch(() => {
          // 首发失败只恢复草稿；依赖更新不能再次自动提交或新建对话。
          initialPendingSendRef.current = null;
          setFailedInitialSend(pendingSend);
          onPendingSendConsumed(pendingSend.id);
        });
    });
    return () => { canceled = true; };
  }, [handleSend, onPendingSendConsumed, onPendingSendSucceeded, pendingSend, scope]);

  useEffect(() => {
    if (!chatError) return;
    const failed = initialPendingSendRef.current;
    if (failed) {
      // SDK 的传输错误也可能通过 chatError 返回，而不 reject sendMessage。
      initialPendingSendRef.current = null;
      queueMicrotask(() => setFailedInitialSend(failed));
    }
    restoreSubmittedAttachments();
  }, [chatError, restoreSubmittedAttachments]);

  const resourceContextBar = selectResourceContextBar(
    resourceAvailabilityQuery.data,
  );
  const currentResourceKey = resourceAvailabilityQuery.data
    ? resourceAvailabilityDismissKey(resourceAvailabilityQuery.data)
    : "";
  const showResource = Boolean(
    resourceContextBar && dismissedResourceKey !== currentResourceKey,
  );
  const currentResourceGate = latestConversationResourceGate(renderTurns);

  const retryResourceGate = async (messageId: string) => {
    if (retryingMessageId) return;
    setRetryingMessageId(messageId);
    try {
      const refreshed = await resourceAvailabilityQuery.refetch();
      if (
        refreshed.data?.aiCredit.state === "healthy" ||
        refreshed.data?.aiCredit.state === "low"
      ) {
        await regenerate({ messageId });
      }
    } finally {
      setRetryingMessageId("");
    }
  };

  const continueLimitedAnswer = async () => {
    if (!lengthLimitedTurn || continuationInFlightRef.current ||
      isConversationProcessing ||
      (continuationAlreadySubmitted && !continuationCanRetry)) return;
    if (!resourceCapabilities.canSendAI) {
      setContinuationNotice("AI 使用额度已用完，补充额度后可继续回答。");
      return;
    }
    continuationInFlightRef.current = lengthLimitedTurn.turnId;
    setContinuationNotice("");
    const messageId = continuationCanRetry
      ? continuationSubmittedUserId
      : crypto.randomUUID();
    activeOutboundMessageIdRef.current = messageId;
    setReconciliationTimedOutTurnId("");
    setReconciliationTurnId(messageId);
    try {
      if (continuationCanRetry) {
        // 首次传输可能未被服务端接收；原位重发完整消息，沿用同一 ID。
        await sendMessage({
          messageId,
          metadata: {
            continuation_of_turn_id: lengthLimitedTurn.turnId,
            resources: [],
          },
          parts: [{ text: "请从上一条未完成的回答中断处继续，只补充尚未回答的内容，不重复已写部分，并遵守我原本指定的字数限制。", type: "text" }],
          role: "user",
        });
      } else {
        await sendMessage({
          id: messageId,
          metadata: {
            continuation_of_turn_id: lengthLimitedTurn.turnId,
            resources: [],
          },
          parts: [{ text: "请从上一条未完成的回答中断处继续，只补充尚未回答的内容，不重复已写部分，并遵守我原本指定的字数限制。", type: "text" }],
          role: "user",
        });
      }
    } catch {
      activeOutboundMessageIdRef.current = "";
      setReconciliationTurnId((current) =>
        clearConversationReconciliationTurn(current, messageId),
      );
      setContinuationNotice("续写没有发送成功，请重试。");
    } finally {
      continuationInFlightRef.current = "";
    }
  };

  return (
    <FormalConversationView
      presentation={presentation}
      stickyLevel={stickyState.level}
      title={conversationTitle}
      titleRef={titleRef}
      scrollRef={scrollRef}
      titleSentinelRef={titleSentinelRef}
      onReset={onReset}
      onKeyUp={quoteSelection.handleKeyUp}
      onMouseUp={quoteSelection.handleMouseUp}
      onScroll={quoteSelection.clear}
      items={minimapItems}
      onNavigate={(turnId) => {
        pauseFollowing();
        const turn = serverTurns.find((candidate) => candidate.turnId === turnId);
        if (turn) setTargetTurn({ id: turn.userMessageId, startSequence: turn.startSequence });
        else if (renderTurns.some((item) => item.id === turnId)) setTargetTurn({ id: turnId, startSequence: 0 });
      }}
      streamStatus={status}
      content={<>
        {merchantHomeOperationId ? (
          <MerchantHomeIntentStatus
            key={merchantHomeOperationId}
            operationId={merchantHomeOperationId}
            organizationId={scope.organizationId}
            userId={scope.userId}
          />
        ) : null}

        {presentation === "workspace" ? (
          <ConversationUnassignedShopNotice
            canSendAI={resourceCapabilities.canSendAI}
            conversationShopBindingId={frozenShopBindingId}
            organizationId={scope.organizationId}
          />
        ) : null}

        {presentation === "workspace" ? (
          <ConversationFreshTaskStatus
            onReadAgain={() => { void freshTasksQuery.refetch(); }}
            stale={freshTasksQuery.isError}
            tasks={freshTasksQuery.currentData ?? []}
          />
        ) : null}

        {messagesQuery.hasNextPage ? (
          <AriaButton
            className="merry-conversation__load-earlier"
            isDisabled={messagesQuery.isFetchingNextPage}
            onPress={() => void loadOlderMessages()}
          >
            {messagesQuery.isFetchingNextPage ? "正在读取…" : "读取更早的对话"}
          </AriaButton>
        ) : null}

        {sourceSeekUnavailable ? (
          <p className="merry-conversation__source-seek-error" role="status">
            无法定位这条策略的来源，对话仍可正常查看。
          </p>
        ) : null}

        <div className="merry-conversation__turns">
          {(presentation === "workspace" ? mediaOwnership.turns : renderTurns).map((turn) => (
            <article
              className="merry-conversation-turn"
              data-conversation-turn-id={turn.id}
              data-conversation-ruler-id={serverTurns.find((item) => item.userMessageId === turn.id)?.turnId ?? turn.id}
              data-source-target={highlightedTurnId === turn.id || undefined}
              id={`conversation-turn-${turn.id}`}
              key={turn.id}
            >
              <div aria-hidden="true" data-conversation-turn-sentinel="" />
              <ConversationUserCard key={`${scope.organizationId}:${scope.conversationId}:${turn.id}`} turnId={turn.id}
                stuck={stickyState.activeTurnId === turn.id && stickyState.level === 3}
                compactSticky={presentation === "workspace"} summary={turn.userMessage.text || "已发送附件"}
                resources={presentation === "workspace" ? turn.userMessage.resources : []}
                organizationId={scope.organizationId} onOpenResource={openCloudResource}>
                {turn.userMessage.quote ? (
                  <blockquote className="merry-conversation-turn__quoted-source">
                    <MerryQuoteDisclosure text={turn.userMessage.quote.text} />
                  </blockquote>
                ) : null}
                <ConversationUserExtensions
                  extensions={turn.userMessage.extensions}
                  snapshots={turn.userMessage.extensionSnapshots}
                />
                <p>{turn.userMessage.text || "已发送附件"}</p>
              </ConversationUserCard>
              <div className="merry-conversation-turn__assistant-list">
                {turn.assistantMessages.map((message) => (
                  <div className="merry-conversation-turn__assistant" key={message.id}>
                    <ConversationAssistantResponse
                      conversationId={scope.conversationId}
                      contentMode={
                        presentation === "temporary-store-overview"
                          ? "read-only"
                          : "standard"
                      }
                      contentHash={message.contentHash}
                      createdAt={message.createdAt}
                      creativeMediaParts={message.creativeMediaParts}
                      executionNodes={message.executionNodes}
                      merchantHomeBindingParts={message.merchantHomeBindingParts}
                      experienceStrategyProposalParts={
                        message.experienceStrategyProposalParts
                      }
                      isLatestCompleted={latestCompletedAssistantId === message.id}
                      message={message.text}
                      messageId={message.id}
                      onOpenMediaCanvas={(jobId, resourceId) => {
                        navigate(
                          `/workspace/conversations/${encodeURIComponent(scope.conversationId)}/creative-media/${encodeURIComponent(jobId)}`,
                          {
                            state: {
                              conversationId: scope.conversationId,
                              initialResourceId: resourceId ?? "",
                            },
                          },
                        );
                      }}
                      onAdjustExperienceStrategy={(reference) => {
                        setPendingStrategyAdjustment(reference);
                        setPendingQuote(null);
                        setFocusRequestKey((current) => current + 1);
                      }}
                      onQuote={(text, sourceProjectionText) => {
                        if (!message.contentHash || !message.sequence || !text) return;
                        setPendingQuote({
                          endOffset: text.length,
                          sourceContentHash: message.contentHash,
                          sourceMessageId: message.id,
                          sourceProjection: "rendered_text.v1",
                          sourceProjectionText,
                          sourceSequence: message.sequence,
                          startOffset: 0,
                          text,
                        });
                        setFocusRequestKey((current) => current + 1);
                      }}
                      organizationId={scope.organizationId}
                      sequence={message.sequence}
                      shopBindingId={scope.shopBindingId}
                    />
                    {message.resourceGate ? (
                      <ConversationResourceLimitResult
                        currentRefreshesAt={resourceAvailabilityQuery.data?.aiCredit.refreshesAt}
                        gate={message.resourceGate}
                        isRetrying={retryingMessageId === message.id}
                        onRetry={() => void retryResourceGate(message.id)}
                      />
                    ) : null}
                  </div>
                ))}
                {turn.assistantMessages.length === 0 &&
                isConversationProcessing &&
                (!reconciliationTurnId || turn.id === reconciliationTurnId) ? (
                  <div aria-busy="true" role="status">
                    <span className="sr-only">Merry 正在处理</span>
                    <AssistantResponseSkeleton />
                  </div>
                ) : null}
                {presentation === "workspace" ? (
                  <ConversationWorkflowProgress
                    onOpenCloudResource={openCloudResource}
                    onOpenMediaCanvas={openMediaCanvas}
                    onReadAgain={() => { void workflowsQuery.refetch(); }}
                    onRetryStep={(workflow, step) => { void retryFailedWorkflowStep(workflow, step); }}
                    retryError={workflowsByTurn.get(turn.id)?.workflow_id === workflowRetryNoticeId ? workflowRetryNotice : ""}
                    retryingStepId={retryingWorkflowStepId}
                    stale={workflowsQuery.isError}
                    workflow={workflowsByTurn.get(turn.id)}
                  />
                ) : null}
                {presentation === "workspace" ? <ConversationCreativeMediaProgress
                  conversationId={scope.conversationId} organizationId={scope.organizationId}
                  monotonicNow={mediaProgress.monotonicNow} stale={mediaProgress.failed}
                  onReadAgain={mediaProgress.retryRead} onOpenCanvas={openMediaCanvas}
                  shopBindingId={scope.shopBindingId} slots={mediaOwnership.byTurn.get(turn.id) ?? []} /> : null}
              </div>
            </article>
          ))}
        </div>
        {presentation === "workspace" && lengthLimitedTurn && (!continuationAlreadySubmitted || continuationCanRetry) ? (
          <div className="merry-conversation__continuation" role="status">
            <span>{continuationCanRetry ? "续写暂时未完成，可以重试同一条提问。" : "上一条回答达到长度上限，内容尚未完成。"}</span>
            <AriaButton
              isDisabled={isConversationProcessing || !resourceCapabilities.canSendAI}
              onPress={() => void continueLimitedAnswer()}
            >
              {continuationCanRetry ? "重试续写" : "继续回答"}
            </AriaButton>
          </div>
        ) : null}
        {presentation === "workspace" && reconciliationTimedOut ? (
          <div className="merry-conversation__continuation" role="status">
            <span>状态仍在同步，可重新读取。</span>
            <AriaButton
              onPress={() => {
                setReconciliationTimedOutTurnId("");
                setReconciliationRetryKey((current) => current + 1);
              }}
            >
              重新读取状态
            </AriaButton>
          </div>
        ) : null}
        {presentation === "workspace" && failedFinishTurn && failedFinishTurn.finishReason !== "length" ? (
          <p className="merry-conversation__error" role="status">
            {failedFinishTurn.finishReason === "content-filter"
              ? "回答被内容过滤中断，请调整提问后重新发送。"
              : "模型未确认正常结束，本轮回答未完成；请重试或重新提问。"}
          </p>
        ) : null}
        {continuationNotice ? <p className="merry-conversation__error" role="alert">{continuationNotice}</p> : null}
        {presentation === "workspace" && mediaOwnership.unassigned.length > 0 ?
          <section className="conversation-creative-media-unassigned" aria-label="未定位原始轮次的历史媒体任务">
            <h2>历史媒体任务（未定位原始轮次）</h2>
            <ConversationCreativeMediaProgress conversationId={scope.conversationId} organizationId={scope.organizationId}
              monotonicNow={mediaProgress.monotonicNow} stale={mediaProgress.failed}
              onReadAgain={mediaProgress.retryRead} onOpenCanvas={openMediaCanvas}
              shopBindingId={scope.shopBindingId} slots={mediaOwnership.unassigned} />
          </section> : null}
        {presentation === "workspace" && mediaProgress.failed ?
          <p className="conversation-creative-media-sync-notice" role="status">
            媒体状态暂未同步，最后一次状态仅供参考。
            <AriaButton onPress={mediaProgress.retryRead}>重新读取状态</AriaButton>
          </p> : null}

        {chatError && !currentResourceGate ? (
          <p className="merry-conversation__error" role="alert">
            对话暂时未完成：{chatError.message}
          </p>
        ) : null}
      </>}
      composer={<>
        {pendingStrategyAdjustment ? (
          <div
            className="merry-conversation__strategy-adjustment-context"
            role="status"
          >
            <span>
              {pendingStrategyAdjustment.schema ===
              "experience_strategy_adjustment_ref.v1"
                ? "正在引用已确认策略"
                : "正在引用待确认策略方案"}
            </span>
            <AriaButton
              aria-label="移除经验策略调整引用"
              onPress={() => {
                setPendingStrategyAdjustment(null);
                onStrategyAdjustmentConsumed();
              }}
            >
              ×
            </AriaButton>
          </div>
        ) : pendingQuote ? (
          <MerryChatContextBar
            content={{ text: pendingQuote.text, type: "quote" }}
            onDismiss={() => setPendingQuote(null)}
          />
        ) : resourceContextBar && showResource ? (
          <MerryChatContextBar
            content={resourceContextBar}
            onDismiss={() => setDismissedResourceKey(currentResourceKey)}
            onRecharge={(view) => navigate(`/settings/billing?view=${view}`)}
          />
        ) : null}
        <ChatAddMenuComposer
          key={failedInitialSend ? `${failedInitialSend.id}:failed` : "conversation-composer"}
          attachments={attachments}
          className="merry-conversation__composer"
          contentMode={
            presentation === "temporary-store-overview"
              ? "resources-only"
              : "all"
          }
          defaultValue={
            failedInitialSend?.text ?? (pendingStrategyAdjustment
              ? experienceStrategyAdjustmentPrompt(pendingStrategyAdjustment)
              : "")
          }
          defaultExtensions={failedInitialSend?.context.extensions}
          focusRequestKey={focusRequestKey}
          isProcessing={isConversationProcessing}
          isAISendDisabled={
            isConversationProcessing ||
            !resourceCapabilities.canSendAI
          }
          isUploadDisabled={isUploadDisabled}
          speechScopeKey={`${scope.organizationId}:${scope.conversationId}`}
          onAttachmentsChange={onAttachmentsChange}
          onRetryAttachment={onRetryAttachment}
          onUploadFiles={
            presentation === "temporary-store-overview"
              ? undefined
              : onUploadFiles
          }
          onSend={async (text, context) => {
            const failed = failedInitialSend;
            setFailedInitialSend(null);
            try { await handleSend(text, context); }
            catch (error) { if (failed) setFailedInitialSend(failed); throw error; }
          }}
          onStop={() => void stop()}
        />
      </>}
      overlay={<>
      {quoteSelection.action ? (
        <AriaButton
          className="merry-conversation__selection-action"
          onPress={() => {
            const draft = quoteSelection.action?.draft;
            if (!draft) return;
            setPendingQuote(draft);
            quoteSelection.clear();
            setFocusRequestKey((current) => current + 1);
          }}
          style={{
            left: quoteSelection.action.left,
            top: quoteSelection.action.top,
          }}
        >
          询问 Merry
        </AriaButton>
      ) : null}
      </>}
    />
  );
}

function ConversationUserExtensions({
  extensions, snapshots = [],
}: {
  extensions: ReadonlyArray<MerryChatExtensionViewModel>;
  snapshots?: ReadonlyArray<{ id: string; label: string; text: string }>;
}) {
  const unknown = snapshots.filter((snapshot) => !extensions.some((extension) => extension.definitionId === snapshot.label));
  if (extensions.length === 0 && unknown.length === 0) return null;

  return (
    <div
      aria-label="本轮使用的能力和工具"
      className="merry-conversation-turn__extensions"
      data-testid="conversation-user-extensions"
      role="group"
    >
      {extensions.map((extension) => (
        <DialogTrigger key={extension.id}>
          <AriaButton className="merry-conversation-turn__extension" data-chat-extension-kind={extension.kind}>
            <img alt="" aria-hidden="true" src={extension.icon} />
            <span>{extension.label}</span>
            {extension.summary ? <span className="merry-conversation-turn__extension-summary">{extension.summary}</span> : null}
          </AriaButton>
          <Popover placement="bottom start" className="conversation-sent-parameters">
            <Dialog aria-label={`${extension.label} · 发送时参数`}>
              <h2>{extension.label} · 发送时参数</h2>
              <p>本轮使用的参数</p>
              <dl>{extension.parameters ? Object.entries(extension.parameters).filter(([key]) => key !== "type").map(([key, value]) =>
                <div key={key}><dt>{{ aspectRatio: "画面比例", longEdge: "长边像素", duration: "视频时长（秒）", resolution: "分辨率", sound: "声音" }[key] ?? key}</dt>
                  <dd>{value === "with-sound" ? "有声" : value === "muted" ? "静音" : String(value)}</dd></div>) : <div><dt>配置</dt><dd>此工具没有附加参数</dd></div>}</dl>
            </Dialog>
          </Popover>
        </DialogTrigger>
      ))}
      {unknown.map((snapshot) => <DialogTrigger key={snapshot.id}>
        <AriaButton className="merry-conversation-turn__extension">{snapshot.label} · 历史参数</AriaButton>
        <Popover className="conversation-sent-parameters"><Dialog aria-label={`${snapshot.label} · 历史参数`}>
          <h2>发送时参数</h2><pre>{snapshot.text}</pre>
        </Dialog></Popover>
      </DialogTrigger>)}
    </div>
  );
}

function mergeConversationHistory(
  primary: ReadonlyArray<ConversationMessage>,
  window: ReadonlyArray<ConversationMessage>,
) {
  const messages = new Map<string, ConversationMessage>();
  for (const message of [...window, ...primary]) messages.set(message.id, message);
  return [...messages.values()].sort((left, right) =>
    left.sequence === right.sequence
      ? left.id.localeCompare(right.id)
      : left.sequence - right.sequence,
  );
}
